<template>
  <header class="tc-app-nav">
    <span class="tc-app-nav-brand">TimeCapture</span>
    <div class="tc-app-nav-spacer"></div>

    <!-- Sources control (ADR-0029 §5) — the globe opens the modal and carries the
         attention signal; connected sources show as tiles beside it. -->
    <div class="tc-app-nav-sources d-flex align-items-center gap-1">
      <button
        type="button"
        class="tc-btn-icon tc-sources-nav-btn"
        :class="{
          'tc-sources-nav-btn--info': badgeState === 'info',
          'tc-sources-nav-btn--amber': badgeState === 'amber',
          'tc-sources-nav-btn--red': badgeState === 'red',
        }"
        title="Sources"
        aria-label="Sources"
        data-testid="sources-nav-btn"
        @click="sourcesStore.openModal()"
      >
        <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <circle cx="9" cy="9" r="7" stroke="currentColor" stroke-width="1.3"/>
          <path d="M9 2C9 2 6 5 6 9s3 7 3 7M9 2c0 0 3 3 3 7s-3 7-3 7M2 9h14" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
        </svg>
        <span v-if="badgeState === 'red'" class="tc-sources-badge tc-sources-badge--red" aria-live="polite">
          Sources &middot; {{ reauthCount }} disconnected
        </span>
        <span v-else-if="badgeState === 'amber'" class="tc-sources-badge tc-sources-badge--amber" aria-label="Source connection issue" aria-live="polite"></span>
        <span v-else-if="badgeState === 'info'" class="tc-sources-badge tc-sources-badge--info" aria-live="polite">
          Connect a source
        </span>
      </button>

      <div v-if="connected.length" class="tc-source-stack" data-testid="nav-source-stack">
        <SourceBadge v-for="id in connected" :key="id" :source="id" variant="compact" />
      </div>
    </div>

    <slot />
  </header>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { getSources } from '../api/sources';
import { sourcesBadgeState, type SourcesBadgeState } from '../connectors/badge';
import { useSourcesStore } from '../stores/sources';
import SourceBadge from './ui/SourceBadge.vue';
import type { SourceId } from '../api/calendar';

const sourcesStore = useSourcesStore();

// Live source state (ADR-0029 §4). A connect invalidates ['sources'] via the
// sources store, so the globe signal and the tiles refresh together.
const sourcesQuery = useQuery({
  queryKey: ['sources'],
  queryFn: getSources,
  staleTime: 30_000,
  retry: false,
});

const sources = computed(() => sourcesQuery.data.value?.sources ?? []);
const reauthCount = computed(() => sources.value.filter((s) => s.status === 'reauth_required').length);
const badgeState = computed<SourcesBadgeState>(() => sourcesBadgeState(sources.value));
const connected = computed<SourceId[]>(() =>
  sources.value.filter((s) => s.status === 'connected').map((s) => s.id as SourceId),
);
</script>
